import { Box, Divider, FormHelperText, IconButton, Menu, MenuItem } from '@mui/material';
import { styled } from '@mui/material/styles';
import { Editor, EditorConsumer, EditorContent, EditorContext, EditorProvider, useCurrentEditor, useEditor } from '@tiptap/react';
import StarterKit from '@tiptap/starter-kit';
import './RichTextEditor.css';
import FormatBoldIcon from '@mui/icons-material/FormatBold';
import FormatItalicIcon from '@mui/icons-material/FormatItalic';
import FormatListBulletedIcon from '@mui/icons-material/FormatListBulleted';
import FormatListNumberedIcon from '@mui/icons-material/FormatListNumbered';
import RedoIcon from '@mui/icons-material/Redo';
import UndoIcon from '@mui/icons-material/Undo';
import { ReactNode, useCallback, useEffect, useMemo, useRef, useState } from 'react';
import Mention from '@tiptap/extension-mention';
import { MentionUser, mkSuggestion } from './suggestion';
import TaskList from '@tiptap/extension-task-list';
import TaskItem from '@tiptap/extension-task-item';
import Table from '@tiptap/extension-table';
import TableRow from '@tiptap/extension-table-row';
import TableHeader from '@tiptap/extension-table-header';
import TableCell from '@tiptap/extension-table-cell';
import { Localized } from '@fluent/react';
import TableRowsIcon from '@mui/icons-material/TableRows';
import Document from '@tiptap/extension-document';
import Paragraph from '@tiptap/extension-paragraph';
import Text from '@tiptap/extension-text';
import FormatQuoteIcon from '@mui/icons-material/FormatQuote';
import AttachFileIcon from '@mui/icons-material/AttachFile';
import CodeIcon from '@mui/icons-material/Code';
import FormatAlignLeftIcon from '@mui/icons-material/FormatAlignLeft';
import FormatAlignCenterIcon from '@mui/icons-material/FormatAlignCenter';
import FormatAlignRightIcon from '@mui/icons-material/FormatAlignRight';
import FormatAlignJustifyIcon from '@mui/icons-material/FormatAlignJustify';
import TextAlign from '@tiptap/extension-text-align';

//TODO: incorporate tiptap-extension-resizable-image into cobdebase and remove from package.json
import 'tiptap-extension-resizable-image/styles.css';
import { ResizableImage } from 'tiptap-extension-resizable-image';
import Placeholder from '@tiptap/extension-placeholder';
import { BlobMeta } from '../features/documents/attachementsSlice';
import { BlobImageNode, BlobLinkExtension } from './tiptapBlob';
import { isFileAnImage } from '../features/urs/Attachements';
import { CodeBlock } from './tiptapCode';

export interface RichTextEditorProps {
    value         : string
    onValueChange : (newValue : string) => void
    label         : React.ReactNode | null
    uploadFile?    : (fs : File[]) => Promise<[string,BlobMeta]>
    placeholder?  : string
    editable?     : boolean
}

const BoxWithBorder = styled('div')(({ theme }) => ({
    borderRadius: theme.shape.borderRadius,
    border: `1px solid ${theme.palette.divider}`,
    padding: theme.spacing(2, 2),
}));

// The alignment buttons, in toolbar order. `left` is the implicit default:
// TextAlign is configured without `defaultAlignment`, so an unaligned block
// carries no attribute and serializes to unchanged HTML.
const TEXT_ALIGNMENTS = [
    { value: 'left', Icon: FormatAlignLeftIcon },
    { value: 'center', Icon: FormatAlignCenterIcon },
    { value: 'right', Icon: FormatAlignRightIcon },
    { value: 'justify', Icon: FormatAlignJustifyIcon },
] as const

interface MenuBarProps {
    uploadFile?    : (fs : File[]) => Promise<[string,BlobMeta]>
    actions?: ReactNode | undefined
}
const MenuBar = (props: MenuBarProps) => {
    const { uploadFile, actions } = props
    const { editor } = useCurrentEditor()

    const fileInputRef = useRef<HTMLInputElement | null>(null)
    const formRef = useRef<HTMLFormElement | null>(null)

    const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
    const open = Boolean(anchorEl);
    const handleTableClick = (event: React.MouseEvent<HTMLButtonElement>) => {
        setAnchorEl(event.currentTarget)
    }
    const handleTableClose = () => {
        setAnchorEl(null)
    }

    const handleSelectFilesClick = useCallback(() => {
        if (fileInputRef) {
            fileInputRef.current?.click()
        }
    }, [])
    const handleInputFileChange = useCallback(async () => {
        if (fileInputRef.current && editor) {
            const dt = fileInputRef.current.files
            if (dt && dt.length > 0) {
                for (const file of dt) {
                    if (isFileAnImage(file)) {
                        const reader = new FileReader()
                        reader.onload = () => {
                            const imgSrc = reader.result as string
                            editor.chain().focus().setResizableImage({ src: imgSrc }).run()
                        }
                        reader.readAsDataURL(file)
                    } else if (uploadFile) {
                        uploadFile([file])
                            .then(([_resId, bm]) => {
                                // Store only the blobId; BlobLinkExtension mints a signed URL on click.
                                //TODO: fix space added at the end as workaround
                                // without space tiptap is not abe to recognize when is end of label for a element
                                const link = `<a href="#" data-blob-id="${bm.blobId}">${bm.fileMeta.fileName}</a> `
                                const pos = editor.state.selection.$anchor.pos
                                editor.chain().focus().insertContentAt(pos, link).run()
                            })
                    }
                }
            }
        }
        if (formRef.current) {
            formRef.current.reset()
        }
    }, [])

    if (!editor) {
        return null
    }

    const handleAddNewTableClick = () => {
        editor.chain().focus().insertTable({ rows: 3, cols: 3, withHeaderRow: true }).run()
        setAnchorEl(null)
    }
    const handleDeleteTableClick = () => {
        editor.chain().focus().deleteTable().run()
        setAnchorEl(null)
    }
    const handleAddColumnAfterClick = () => {
        editor.chain().focus().addColumnAfter().run()
        setAnchorEl(null)
    }
    const handleAddColumnBeforeClick = () => {
        editor.chain().focus().addColumnBefore().run()
        setAnchorEl(null)
    }
    const handleAddRowAfterClick = () => {
        editor.chain().focus().addRowAfter().run()
        setAnchorEl(null)
    }
    const handleAddRowBeforeClick = () => {
        editor.chain().focus().addRowBefore().run()
        setAnchorEl(null)
    }
    const handleDeleteColumnClick = () => {
        editor.chain().focus().deleteColumn().run()
        setAnchorEl(null)
    }
    const handleDeleteRowClick = () => {
        editor.chain().focus().deleteRow().run()
        setAnchorEl(null)
    }
    const handleMergeCellsClick = () => {
        editor.chain().focus().mergeCells().run()
        setAnchorEl(null)
    }
    const handleSplitCellClick = () => {
        editor.chain().focus().splitCell().run()
        setAnchorEl(null)
    }
    const handleToggleHeaderCellClick = () => {
        editor.chain().focus().toggleHeaderCell().run()
        setAnchorEl(null)
    }

    const handleSetAlignmentClick = (alignment: string) => {
        // `left` is the absence of an alignment, not a value to stamp on the node,
        // so it clears the attribute instead of writing `text-align: left`.
        if (alignment === 'left') {
            editor.chain().focus().unsetTextAlign().run()
        } else {
            editor.chain().focus().setTextAlign(alignment).run()
        }
    }

    // Nothing is active while the block is unaligned, which is `left`.
    const currentAlignment = TEXT_ALIGNMENTS.find(a => editor.isActive({ textAlign: a.value })) ?? TEXT_ALIGNMENTS[0]

    const editable = editor.isEditable

    return <Box sx={{ mb: 1, display: 'flex' }}>
        <form ref={formRef}>
            <input
                ref={fileInputRef}
                style={{ display: 'none' }}
                type="file"
                multiple={false}
                value=''
                onChange={handleInputFileChange}
                accept="*"
            />
        </form>
        <IconButton 
            size="small"
            onClick={() => editor.chain().focus().toggleBold().run()}
            disabled={!editable || !editor.can().chain().focus().toggleBold().run()}
            color={editor.isActive('bold') ? 'secondary' : 'primary'}
        ><FormatBoldIcon /></IconButton>
        <IconButton 
            size="small"
            onClick={() => editor.chain().focus().toggleItalic().run()}
            disabled={!editable || !editor.can().chain().focus().toggleItalic().run()}
            color={editor.isActive('italic') ? 'secondary' : 'primary'}
        ><FormatItalicIcon /></IconButton>
        <IconButton 
            size="small"
            disabled={!editable}
            onClick={() => editor.chain().focus().toggleBulletList().run()}
            color={editor.isActive('bulletList') ? 'secondary' : 'primary'}
        ><FormatListBulletedIcon /></IconButton>
        <IconButton 
            size="small"
            disabled={!editable}
            onClick={() => editor.chain().focus().toggleOrderedList().run()}
            color={editor.isActive('orderedList') ? 'secondary' : 'primary'}
        ><FormatListNumberedIcon /></IconButton>
        <IconButton 
            size="small"
            disabled={!editable}
            onClick={() => editor.chain().focus().toggleBlockquote().run()}
            color={editor.isActive('blockquote') ? 'secondary' : 'primary'}
        ><FormatQuoteIcon /></IconButton>

        <IconButton
            size="small"
            disabled={!editable}
            onClick={() => editor.chain().focus().toggleCodeBlock().run()}
            color={editor.isActive('codeBlock') ? 'secondary' : 'primary'}
        ><CodeIcon /></IconButton>

        <Divider orientation='vertical' flexItem sx={{ mx: 0.5, my: 0.5 }} />
        {TEXT_ALIGNMENTS.map(({ value, Icon }) =>
            <IconButton
                key={value}
                size="small"
                disabled={!editable}
                onClick={() => handleSetAlignmentClick(value)}
                color={value === currentAlignment.value ? 'secondary' : 'primary'}
            ><Icon /></IconButton>
        )}
        <Divider orientation='vertical' flexItem sx={{ mx: 0.5, my: 0.5 }} />

        {uploadFile && <IconButton
            size='small'
            disabled={!editable}
            onClick={handleSelectFilesClick}
        ><AttachFileIcon fontSize='small' /></IconButton>}
        <IconButton size='small' onClick={handleTableClick}>
            <TableRowsIcon />
        </IconButton>
        <IconButton 
            size="small"
            onClick={() => editor.chain().focus().undo().run()}
            disabled={!editable || !editor.can().chain().focus().undo().run()}
        ><UndoIcon /></IconButton>
        <IconButton 
            size="small"
            onClick={() => editor.chain().focus().redo().run()}
            disabled={!editable || !editor.can().chain().focus().redo().run()}
        ><RedoIcon /></IconButton>
        <Menu anchorEl={anchorEl} open={open} onClose={handleTableClose}>
            <MenuItem disabled={!editable} key='create' onClick={handleAddNewTableClick}><Localized id='table-add'>Add table</Localized></MenuItem>
            <MenuItem disabled={!editable} key='delete' onClick={handleDeleteTableClick}><Localized id='table-delete'>Delete table</Localized></MenuItem>
            <Divider />
            <MenuItem disabled={!editable} key='add-col-before' onClick={handleAddColumnBeforeClick}><Localized id ='table-add-col-before'>Add col before</Localized></MenuItem>
            <MenuItem disabled={!editable} key='add-col-after' onClick={handleAddColumnAfterClick}><Localized id='table-add-col-after'>Add col after</Localized></MenuItem>
            <MenuItem disabled={!editable} key='delete-col' onClick={handleDeleteColumnClick}><Localized id='table-delete-col'>Delete col</Localized></MenuItem>
            <Divider />
            <MenuItem disabled={!editable} key='add-row-before' onClick={handleAddRowBeforeClick}><Localized id='table-add-row-before'>Add row before</Localized></MenuItem>
            <MenuItem disabled={!editable} key='add-row-after' onClick={handleAddRowAfterClick}><Localized id='table-add-row-after'>Add row after</Localized></MenuItem>
            <MenuItem disabled={!editable} key='delete-row' onClick={handleDeleteRowClick}><Localized id='table-delete-row'>Delete row</Localized></MenuItem>
            <Divider />
            {/* These three only apply to a cell selection (merge), a merged cell
                (split) or a cell (header toggle), so they lean on `can()`
                rather than on `editable` alone. */}
            <MenuItem
                disabled={!editable || !editor.can().chain().focus().mergeCells().run()}
                key='merge-cells'
                onClick={handleMergeCellsClick}
            ><Localized id='table-merge-cells'>Merge cells</Localized></MenuItem>
            <MenuItem
                disabled={!editable || !editor.can().chain().focus().splitCell().run()}
                key='split-cell'
                onClick={handleSplitCellClick}
            ><Localized id='table-split-cell'>Split cell</Localized></MenuItem>
            <MenuItem
                disabled={!editable || !editor.can().chain().focus().toggleHeaderCell().run()}
                key='toggle-header-cell'
                onClick={handleToggleHeaderCellClick}
            ><Localized id='table-toggle-header-cell'>Toggle header cell</Localized></MenuItem>
        </Menu>
        {actions && <Box sx={{ ml: 'auto'}}>{actions}</Box>}
    </Box>
}

export function mkTipTapExtWithMention(persons: MentionUser[]) {
    return [...tiptapExtensions, Mention.configure({
        suggestion: mkSuggestion(persons),
        HTMLAttributes: {
            class: 'mention',
        },
    })]
}

export const tiptapExtensions = [
    StarterKit.configure({
        // bulletList: {
        //     keepMarks: true,
        //     keepAttributes: false, // TODO : Making this as `false` becase marks are not preserved when I try to preserve attrs, awaiting a bit of help
        // },
        // orderedList: {
        //     keepMarks: true,
        //     keepAttributes: false, // TODO : Making this as `false` becase marks are not preserved when I try to preserve attrs, awaiting a bit of help
        // },
        heading: {
            levels: [1, 2, 3],
        },
    }),
    TaskList,
    TaskItem.configure({
        nested: true,
    }),
    // Image,
    ResizableImage.configure({
        onUpload(file) {
            return new Promise(resolve => {
                const reader = new FileReader()
                reader.onload = () => {
                    resolve({
                        src: reader.result as string
                    })
                }
                reader.readAsDataURL(file)
            })
        },
    }),
    // Renders blob-backed <img> (incl. legacy absolute-URL ones) via a signed URL.
    BlobImageNode,
    BlobLinkExtension.configure({
        openOnClick: false,
    }),
    // No `defaultAlignment`: an unaligned block keeps a null attribute and renders
    // no style, so content written before this extension round-trips unchanged.
    TextAlign.configure({
        types: ['heading', 'paragraph'],
    }),
    Table.configure({
        resizable: true
    }),
    TableRow,
    TableHeader,
    TableCell,
    CodeBlock,
]

export const RichTextEditor = (props: RichTextEditorProps) => {
    const { value, onValueChange, label, uploadFile, placeholder, editable = true } = props

    // A copy, never `tiptapExtensions` itself: pushing onto the shared array would
    // leak this editor's Placeholder into every other editor built from it, once
    // per render. Memoized because the array identity is an editor option.
    const exts = useMemo(
        () => placeholder
            ? [...tiptapExtensions, Placeholder.configure({ placeholder })]
            : tiptapExtensions,
        [placeholder],
    )

    return <Box>
        {label != null && <FormHelperText>{label}</FormHelperText>}
        <BoxWithBorder>
            <EditorProvider
                onUpdate={e => onValueChange(e.editor.getHTML())}
                slotBefore={<MenuBar uploadFile={uploadFile} actions={null} />}
                extensions={exts}
                content={value}
                editable={editable}
            >
                <></>
            </EditorProvider>
        </BoxWithBorder>
    </Box>
}

export interface RichTextContainerProps {
    label         : React.ReactNode | null | undefined
    editor        : Editor | null
    uploadFile?    : (fs : File[]) => Promise<[string,BlobMeta]>
    actions?      : ReactNode | undefined
}
export const RichTextContainer = (props: RichTextContainerProps) => {
    const { label, editor, uploadFile, actions } = props

    if (!editor) return null

    return <Box>
        {label != null && <FormHelperText>{label}</FormHelperText>}
        <BoxWithBorder>
            <EditorContext.Provider value={{editor}}>
                <MenuBar uploadFile={uploadFile} actions={actions} />
                <EditorConsumer>
                    {({ editor: currentEditor }) => (
                        <EditorContent editor={currentEditor} />
                    )}
                </EditorConsumer>
            </EditorContext.Provider>
        </BoxWithBorder>
    </Box>
}

export const RichTextEditorExample = () => {
    const editor = useEditor({
        extensions: tiptapExtensions,
        content: '<p>Initial content</p>',   // initial value
        onUpdate: (e) => {                   // callback to get the updated value
            console.log(e.editor.getHTML())
        },
    })

    useEffect(() => {
        if (editor) {
            editor.commands.setContent('<p>Initial content</p>')
        }
    }, [editor])

    return <RichTextContainer label="Example" editor={editor} />
}

export const useJustText = (props: { defaultValue: string, onChange: () => void, editable? : boolean | undefined, placeholder?: string }) : Editor | null => {
    const { placeholder } = props
    let exts : any[] = [Document, Paragraph, Text]
    if (placeholder) {
        exts.push(
            Placeholder.configure({
                placeholder,
            })
        )
    }
    const editor = useEditor({
        extensions: exts,
        content: props.defaultValue,
        editable: props.editable,
        onUpdate: e => props.onChange(),
    })
    return editor
}

export const JustText = ({ editor }: { editor: Editor | null }) => {
    if (!editor) {
        return null
    }
    return <EditorContent editor={editor} />
}
